<template>
  <main
    role="main"
    class="col content-spacing"
    style="max-width: 88% !important;"
  >
    <section>
      <PageHeader pageTitle="Faturamento" style="padding-top: 1rem;">
        <div style="display: flex; gap: 0.5rem;">
          <SelectMonth v-model="search.month" />
          <SelectYear v-model="search.year" />
          <SelectBillingStatus
            v-model="search.billingStatus"
            v-if="primaryFiltersHasSelected()"
          />
          <SelectPromoter
            :traderList="traderList"
            v-model="search.trader"
            v-if="billingList.length"
          />
          <SelectAccountManager
            v-model="search.accountManagerId"
            v-if="billingList.length"
          />
          <GenericSearchInput
            v-model="search.textSearch"
            v-if="primaryFiltersHasSelected()"
          />
          <VueJsonToCsv block :json-data="CSVBillingList" :separator="';'">
            <BaseButtonComponent
              block
              variant="primary-action"
              :disabled="!CSVBillingList.length"
            >
              CSV
            </BaseButtonComponent>
          </VueJsonToCsv>
        </div>
      </PageHeader>
    </section>

    <BillingStatusSummary
      v-if="billingSummaryList.length"
      :billingSummaryStatus="billingSummaryList"
    />

    <b-skeleton-table
      animation="wave"
      style="margin-top: 2rem;"
      :rows="5"
      :columns="15"
      :table-props="{ bordered: true, striped: true }"
      v-if="isLoading"
    />

    <section
      class="d-flex justify-content-between flex-column"
      style="height: 100%;"
    >
      <Table class="billing-table" v-if="!isLoading && billingList.length">
        <TableHeader>
          <TableHead
            v-for="(billingCol, index) of billingColumns"
            :key="`${billingCol.name}_${index}`"
          >
            <ColumnHeader
              :sort="billingCol.sort"
              :somatory="billingCol.somatory"
              :columnName="billingCol.name"
              :columnKey="billingCol.key"
              :columnType="billingCol.columnType"
              :currentColumnFiltered="currentColumnFiltered"
              :sortDirection="sortDirection"
              :dataList="billingList"
              v-on:sortListByKey="sortListByKey(billingCol.key)"
            />
          </TableHead>
        </TableHeader>
        <TableBody>
          <TableRow
            v-for="(billing, index) of billingList"
            :key="`${billing.installationNumber}_${index}`"
          >
            <TableCell>
              {{ billing.installationNumber }}
            </TableCell>
            <TableCell>
              <RenderNameColumnValue :name="billing.name" />
            </TableCell>
            <TableCell>
              <RenderInstallationStatusColumnValue
                :value="billing.installationStatus"
              />
            </TableCell>
            <TableCell>
              <RenderBillingStatusColumnValue :value="billing.billingStatus" />
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderMoneyColumnValue :value="billing.boletoVeins" />
            </TableCell>
            <TableCell>
              <RenderDateColumnValue :value="billing.dueDate" />
            </TableCell>
            <TableCell>
              <span :style="setDelayedColor(billing.days)">{{
                billing.days
              }}</span>
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderMoneyColumnValue :value="billing.valueReceived" />
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderMoneyColumnValue :value="billing.fine" />
            </TableCell>
            <TableCell style="text-align: end;">
              {{ billing.paymentDiscount }}
            </TableCell>
            <TableCell>
              {{ billing.compliance }}
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderMoneyColumnValue :value="billing.totalCredit" />
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderMoneyColumnValue :value="billing.consumerEconomy" />
            </TableCell>
            <TableCell style="text-align: end;">
              {{ billing.percentagePowerPlant }}
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderMoneyColumnValue :value="billing.repasseUsinaValue" />
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderMoneyColumnValue :value="billing.grossIncomeValue" />
            </TableCell>
            <TableCell style="text-align: end;">
              {{ billing.energyTariff }}
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderMoneyColumnValue :value="billing.distributorInvoice" />
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderKwhColumnValue :value="billing.totalConsumption" />
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderKwhColumnValue :value="billing.distributionEnergy" />
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderKwhColumnValue :value="billing.compensatedEnergy" />
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderKwhColumnValue :value="billing.accumulatedBalance" />
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderKwhColumnValue :value="billing.lastAccumulatedBalance" />
            </TableCell>
            <TableCell
              :style="
                setDeltaColorIfNegativeValue(billing.deltaAccumulatedBalance)
              "
            >
              <RenderKwhColumnValue :value="billing.deltaAccumulatedBalance" />
            </TableCell>
            <TableCell>
              <DownloadInvoiceButton
                :invoice="{
                  nossoNumero: billing.nossoNumero,
                  codigoSolicitacao: billing.codigoSolicitacao,
                  name: billing.name,
                  installationNumber: billing.installationNumber,
                  dueDate: billing.dueDate,
                  boletoVeins: billing.boletoVeins,
                }"
                style="padding: 0.1rem 0.8rem;"
              />
            </TableCell>
            <TableCell>
              <RenderNameColumnValue :name="billing.traderName || ''" />
            </TableCell>
            <TableCell>
              <RenderNameColumnValue :name="billing.accountManagerName || ''" />
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderKwhColumnValue :value="billing.exemptEnergyKwh || 0" />
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderKwhColumnValue :value="billing.gdOneEnergyKwh || 0" />
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderKwhColumnValue :value="billing.gdTwoEnergyKwh || 0" />
            </TableCell>
            <TableCell>
              {{ billing.tariffType }}
            </TableCell>
            <TableCell>
              {{ billing.class }}
            </TableCell>
            <TableCell>
              {{
                billing.subClass
                  ? billing.subClass > 11
                    ? `${billing.subClass.slice(0, 11)}...`
                    : billing.subClass
                  : ''
              }}
            </TableCell>
            <TableCell>
              {{ billing.phase }}
            </TableCell>
            <TableCell>
              {{ billing.energyProvider }}
            </TableCell>
            <TableCell>
              {{ billing.zipCode }}
            </TableCell>
            <TableCell>
              {{ billing.city }}
            </TableCell>
            <TableCell>
              {{ billing.district }}
            </TableCell>
            <TableCell>
              {{ billing.address }} {{ billing.number }}
              {{ billing.complement }}
              {{ billing.state }}
            </TableCell>
            <TableCell style="text-align: end;">
              {{ billing.guaranteedDiscount }}
            </TableCell>
            <TableCell style="text-align: end;">
              {{ billing.percentStandardDiscount }}
            </TableCell>
            <TableCell style="text-align: end;">
              {{ billing.percentDiscountCoupon }}
            </TableCell>
            <TableCell style="text-align: end;">
              {{ billing.percentFidelityDiscount }}
            </TableCell>
            <TableCell>
              <RenderNameColumnValue :name="billing.site" />
            </TableCell>
            <TableCell>
              <RenderSocialNumberColumnValue
                :social-number="billing.socialNumber"
              />
            </TableCell>
            <TableCell>
              {{ billing.email }}
            </TableCell>
            <TableCell>
              <RenderPhoneColumnValue :phone-number="billing.phone" />
            </TableCell>
            <TableCell>
              {{ billing.salesInvoice }}
            </TableCell>
            <TableCell>
              <RenderDateColumnValue :value="billing.dataInput" />
            </TableCell>
            <TableCell>
              {{ billing.salesFiscal }}
            </TableCell>
            <TableCell>
              {{ billing.linhaDigitavel }}
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderDateColumnValue :value="billing.sendBoletoDate" />
            </TableCell>
            <TableCell style="text-align: end;">
              <RenderDateColumnValue :value="billing.paidDate" />
            </TableCell>
            <TableCell>
              <BaseButtonComponent
                @click="() => calcConsumptionItem(billing)"
                v-if="
                  billing.billingStatus === 'AWAITING_GEN_CALC' &&
                    userData.profile === PROFILE.admin
                "
                variant="tertiary-action"
                style="padding: 1px 12px;"
                >Gravar Consumo calculado</BaseButtonComponent
              >

              <BaseButtonComponent
                @click="() => generateOVItem(billing)"
                v-if="
                  billing.billingStatus === 'AWAITING_GEN_OV' &&
                    userData.profile === PROFILE.admin
                "
                variant="primary-aciton"
                style="padding: 1px 12px;"
                >Gerar OV</BaseButtonComponent
              >

              <BaseButtonComponent
                @click="() => generateBoletoItem(billing)"
                v-if="
                  billing.billingStatus === 'AWAITING_GEN_BOLETO' &&
                    userData.profile === PROFILE.admin
                "
                variant="primary-action"
                block
                >Gerar Boleto</BaseButtonComponent
              >

              <MarkInvoiceToPaidButton
                v-if="
                  ![
                    'AWAITING_GEN_CALC',
                    'AWAITING_GEN_OV',
                    'AWAITING_GEN_BOLETO',
                    'PAID',
                  ].includes(billing.billingStatus)
                "
                :invoice="billing"
              />

              <b-overlay
                :show="busy"
                rounded
                opacity="0.6"
                spinner-small
                spinner-variant="primary"
                class="d-inline-block"
              >
                <BaseButtonComponent
                  style="padding: 1px 12px;"
                  @click="() => sendEmail(billing)"
                  :disabled="billing.billingStatus === 'PAID'"
                  v-if="
                    (billing.billingStatus === 'WAITING_PAYMENT' ||
                      billing.billingStatus === 'PAID') &&
                      userData.profile === PROFILE.admin
                  "
                  :variant="
                    billing.emailSent
                      ? 'success-confirmattion'
                      : 'primary-action'
                  "
                  >Enviar Email</BaseButtonComponent
                >
              </b-overlay>
            </TableCell>
            <TableCell>
              {{ billing.hasFidelity }}
            </TableCell>
            <TableCell>
              {{ billing.fidelityCounter }}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <empty-list v-else-if="!isLoading" />
    </section>
  </main>
</template>

<script>
/// <reference path="../../modules/invoice/types/index.js" />
/// <reference path="../../modules/common/types/index.js" />

import {
  DownloadInvoiceButton,
  MarkInvoiceToPaidButton,
  SelectBillingStatus,
  BillingStatusSummary,
} from '../../modules/invoice/components';
import { GenericSearchInput } from '../../ui/inputs';
import { PageHeader } from '../../ui/page-header';
import {
  SelectAccountManager,
  SelectMonth,
  SelectPromoter,
  SelectYear,
} from '../../ui/selects';
import VueJsonToCsv from 'vue-json-to-csv';
import {
  formatCurrencyFraction2,
  calculateDaysDifferenceFromNow,
  formatSocialNumber,
  sortByProperty,
  defineArrowDirection,
  formatDateByType,
} from '../../utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { ColumnHeader } from '../../ui/column-header';
import {
  RenderDateColumnValue,
  RenderKwhColumnValue,
  RenderMoneyColumnValue,
  RenderNameColumnValue,
  RenderPhoneColumnValue,
  RenderSocialNumberColumnValue,
  RenderBillingStatusColumnValue,
  RenderInstallationStatusColumnValue,
} from '../../ui/render';
import { Api } from '../../vue/api';
import helpers from '../../vue/helpers';
import { setDelayedDaysColor } from '../../modules/invoice/utils';
import { INVOICE_STATUS } from '../../modules/invoice/constants';
import { Auth } from '../../vue/auth';
import { PROFILE } from '../../vue/routes/constants';
import { BaseButtonComponent } from '../../ui/buttons';
const API = new Api();

export default {
  components: {
    PageHeader,
    SelectMonth,
    SelectYear,
    SelectBillingStatus,
    SelectPromoter,
    SelectAccountManager,
    GenericSearchInput,
    VueJsonToCsv,
    Table,
    TableHeader,
    TableHead,
    ColumnHeader,
    TableBody,
    TableRow,
    TableCell,
    RenderNameColumnValue,
    RenderKwhColumnValue,
    RenderMoneyColumnValue,
    RenderDateColumnValue,
    RenderSocialNumberColumnValue,
    RenderPhoneColumnValue,
    DownloadInvoiceButton,
    RenderBillingStatusColumnValue,
    RenderInstallationStatusColumnValue,
    MarkInvoiceToPaidButton,
    BillingStatusSummary,
    BaseButtonComponent,
  },
  data() {
    return {
      PROFILE,
      isLoading: false,
      search: {
        month: null,
        year: null,
        billingStatus: null,
        trader: null,
        accountManagerId: null,
        textSearch: null,
      },
      sortDirection: 'ASC',
      currentColumnFiltered: 'name',
      traderList: [],
      userData: {},
      busy: false,
      /**
       * @type {Billing[]}
       */
      billingList: [],
      /**
       * @type {CSVBilling[]}
       */
      CSVBillingList: [],
      /**
       * @type {BillingSummary[]}
       */
      billingSummaryList: [],
      /**
       * @type {ColumnProperty[]}
       */
      billingColumns: [
        {
          name: 'Instalação',
          key: 'installationNumber',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Consumidor',
          key: 'name',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Status UC',
          key: 'installationStatus',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Status Cobrança',
          key: 'billingStatus',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Boleto Veins',
          key: 'boletoVeins',
          columnType: '(R$)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Vence',
          key: 'dueDate',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Dias',
          key: 'days',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Valor Pago',
          key: 'valueReceived',
          columnType: '(R$)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Multa',
          key: 'fine',
          columnType: '(R$)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Desconto',
          key: 'paymentDiscount',
          columnType: '(R$)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Adimplência',
          key: 'compliance',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Cred. tot.',
          key: 'totalCredit',
          columnType: '(R$)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Economia',
          key: 'consumerEconomy',
          columnType: '(R$)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Rep. Usina',
          key: 'percentagePowerPlant',
          columnType: '(%)',
          sort: true,
          somatory: false,
        },
        {
          name: 'Rep. Usina',
          key: 'repasseUsinaValue',
          columnType: '(R$)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Rec. Bruta',
          key: 'grossIncomeValue',
          columnType: '(R$)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Tar. Ene.',
          key: 'energyTariff',
          columnType: '(R$)',
          sort: true,
          somatory: false,
        },
        {
          name: 'Valor Distr',
          key: 'distributorInvoice',
          columnType: '(R$)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Cons. Total',
          key: 'totalConsumption',
          columnType: '(kWh)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Ene. Distr.',
          key: 'distributionEnergy',
          columnType: '(kWh)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Ene. Comp.',
          key: 'compensatedEnergy',
          columnType: '(kWh)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Saldo Ac. Atual',
          key: 'accumulatedBalance',
          columnType: '(kWh)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Saldo Ac. Anterior',
          key: 'lastAccumulatedBalance',
          columnType: '(kWh)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Saldo Ac. Delta',
          key: 'deltaAccumulatedBalance',
          columnType: '(kWh)',
          sort: true,
          somatory: true,
        },
        {
          name: 'PDF Veins',
          key: '',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Promotor',
          key: 'traderName',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'CS',
          key: 'accountManagerName',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Isenta',
          key: 'exemptEnergyKwh',
          columnType: '(kWh)',
          sort: true,
          somatory: true,
        },
        {
          name: 'GD1',
          key: 'gdOneEnergyKwh',
          columnType: '(kWh)',
          sort: true,
          somatory: true,
        },
        {
          name: 'GD2',
          key: 'gdTwoEnergyKwh',
          columnType: '(kWh)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Modalidade',
          key: 'tariffType',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Classe',
          key: 'class',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Subclasse',
          key: 'subClass',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Fase',
          key: 'phase',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Distribuidora',
          key: 'energyProvider',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'CEP',
          key: 'zipCode',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Cidade',
          key: 'city',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Bairro',
          key: 'district',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Endereço',
          key: 'address',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Desc. gar.',
          key: 'guaranteedDiscount',
          columnType: '(%)',
          sort: true,
          somatory: false,
        },
        {
          name: 'Desc. pad.',
          key: 'percentStandardDiscount',
          columnType: '(%)',
          sort: true,
          somatory: false,
        },
        {
          name: 'Desc. cup.',
          key: 'percentDiscountCoupon',
          columnType: '(%)',
          sort: true,
          somatory: false,
        },
        {
          name: 'Desc. fid.',
          key: 'percentFidelityDiscount',
          columnType: '(%)',
          sort: true,
          somatory: false,
        },
        {
          name: 'Titular',
          key: 'site',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'CPF',
          key: 'socialNumber',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Email',
          key: 'email',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Celular',
          key: 'phone',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Fatura Financeira',
          key: 'salesInvoice',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: ' Data Input',
          key: 'dataInput',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Fatura Fiscal',
          key: 'salesFiscal',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Cod Boleto',
          key: 'linhaDigitavel',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Envio Boleto',
          key: 'sendBoletoDate',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Pago Em',
          key: 'paidDate',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Etapas',
          key: '',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Fidelidade',
          key: 'hasFidelity',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Fatura',
          key: 'fidelityCounter',
          columnType: '',
          sort: true,
          somatory: false,
        },
      ],
    };
  },
  beforeMount: async function() {
    this.userData = await Auth.getSessionData();
  },
  computed: {
    year() {
      return this.search.year;
    },
    month() {
      return this.search.month;
    },
    billingStatus() {
      return this.search.billingStatus;
    },
    trader() {
      return this.search.trader;
    },
    accountManagerId() {
      return this.search.accountManagerId;
    },
    textSearch() {
      return this.search.textSearch;
    },
  },
  watch: {
    year() {
      this.fetchInformationByPrimaryFilters();
    },
    month() {
      this.fetchInformationByPrimaryFilters();
    },
    billingStatus() {
      this.handleFilterList('billingStatus', 'billingStatus');
    },
    trader() {
      this.handleFilterList('trader', 'trader');
    },
    accountManagerId() {
      this.handleFilterList('accountManagerId', 'accountManagerId');
    },
    textSearch() {
      this.handleFilterList('search', 'textSearch');
    },
  },
  methods: {
    /**
     * @return {function(): void}
     */
    async getAll() {
      this.isLoading = true;
      const { ok, errors, model } = await API.post('/execute', {
        path:
          `/invoice/${this.search.month}/${this.search.year}` +
          helpers.toQueryString(this.$route.query),
        method: 'get',
      });

      if (!ok) {
        this.$bvToast.toast(errors.message, {
          title: `Erro`,
          variant: 'error',
          solid: true,
        });
        this.isLoading = false;
        return;
      }
      const traderList = [];
      for (const invoice of model) {
        if (invoice.traderName) {
          traderList.push(invoice.traderName);
        }
      }
      this.traderList = traderList;
      const billingList = model;
      this.billingList = billingList;
      this.CSVBillingList = this.parseBillingListToCSVBillingList(billingList);
      this.billingSummaryList = this.generateBillingSummaryList(billingList);
      this.isLoading = false;
    },
    /**
     * Método para mapear os dados de fatura para formato CSV
     * @param {Billing[]} billingList
     * @return {function(): CSVBilling[]}
     */
    parseBillingListToCSVBillingList(billingList) {
      return billingList.map(billing => ({
        Instalacao: billing.installationNumber,
        Consumidor: billing.name,
        Cobranca: INVOICE_STATUS[billing.billingStatus] || '-',
        Boleto_Veins: billing.boletoVeins
          ? formatCurrencyFraction2(billing.boletoVeins)
          : '0',
        Vencimento_Veins: billing.dueDate
          ? formatDateByType(billing.dueDate)
          : '-',
        Dias:
          billing.status !== 'PAID' && billing.dueDate
            ? calculateDaysDifferenceFromNow(billing.dueDate)
            : '-',
        Credito_total: billing.totalCredit
          ? formatCurrencyFraction2(billing.totalCredit)
          : '0',
        Desconto_garantido: billing.guaranteedDiscount
          ? formatCurrencyFraction2(billing.guaranteedDiscount)
          : '0',
        Economia: billing.consumerEconomy
          ? formatCurrencyFraction2(billing.consumerEconomy)
          : '0',
        Repasse_Usina_Porcentagem: billing.percentagePowerPlant
          ? formatCurrencyFraction2(billing.percentagePowerPlant)
          : '0',
        Repasse_Usina: billing.repasseUsinaValue
          ? formatCurrencyFraction2(billing.repasseUsinaValue)
          : '0',
        Receita_Bruta_Veins: billing.grossIncomeValue
          ? formatCurrencyFraction2(billing.grossIncomeValue)
          : '0',
        Receita_Bruta_Veins_Porcentagem: billing.grossIncomePercentage
          ? formatCurrencyFraction2(billing.grossIncomePercentage)
          : '0',
        Tarifa_Energia: billing.energyTariff
          ? formatCurrencyFraction2(billing.energyTariff)
          : '0',
        Tarifa_Injetada: billing.injectedTariff
          ? formatCurrencyFraction2(billing.injectedTariff)
          : '0',
        Valor_Distribuidora: billing.distributorInvoice
          ? formatCurrencyFraction2(billing.distributorInvoice)
          : '0',
        Consumo_Total: billing.totalConsumption
          ? formatCurrencyFraction2(billing.totalConsumption)
          : '0',
        Energia_Distribuidora: billing.distributionEnergy
          ? formatCurrencyFraction2(billing.distributionEnergy)
          : '0',
        Energia_Injetada: billing.injectedEnergy
          ? formatCurrencyFraction2(billing.injectedEnergy)
          : '0',
        Energia_Compensada: billing.compensatedEnergy
          ? formatCurrencyFraction2(billing.compensatedEnergy)
          : '0',
        Saldo_acumulado: billing.accumulatedBalance
          ? formatCurrencyFraction2(billing.accumulatedBalance)
          : '0',
        Modalidade_Tarifaria: billing.percentStandardDiscount
          ? formatCurrencyFraction2(billing.percentStandardDiscount)
          : '0',
        Classe: billing.percentDiscountCoupon
          ? formatCurrencyFraction2(billing.percentDiscountCoupon)
          : '0',
        SubClass: billing.subClass || '-',
        Fase: billing.phase || '-',
        Distribuidora: billing.energyProvider || '-',
        Desconto_padrao: billing.percentStandardDiscount
          ? formatCurrencyFraction2(billing.percentStandardDiscount)
          : '0',
        Desconto_Cupom: billing.percentDiscountCoupon
          ? formatCurrencyFraction2(billing.percentDiscountCoupon)
          : '0',
        CPF: formatSocialNumber(billing.socialNumber) || '-',
        Email: billing.email || '-',
        Telefone: billing.phone || '-',
        Fatura_Financeira: billing.salesInvoice || '-',
        Fatura_Fiscal: billing.salesFiscal || '-',
        Cod_Boleto: billing.linhaDigitavel || '-',
        Promotor: billing.traderName || '-',
        Pago_Em: billing.paidDate ? formatDateByType(billing.paidDate) : '-',
      }));
    },
    /**
     * @param {string} columnKey
     * @return {function(): void}
     */
    sortListByKey(columnKey) {
      this.sortDirection = defineArrowDirection(
        this.currentColumnFiltered,
        columnKey,
        this.sortDirection,
      );
      this.currentColumnFiltered = columnKey;
      sortByProperty(this.billingList, columnKey, this.sortDirection);
    },
    /**
     * Método para filtrar a lista
     * @return {function(): void}
     */
    async handleFilterList(queryProp, searchProp) {
      this.$route.query[queryProp] = this.search[searchProp];
      await this.getAll();
    },
    /**
     * Método para buscar as informações caso os filtros principais sejam atendidos
     * @return {function(): void}
     */
    async fetchInformationByPrimaryFilters() {
      if (this.search.year && this.search.month) await this.getAll();
      return;
    },
    primaryFiltersHasSelected() {
      return this.search.year && this.search.month;
    },
    setDelayedColor(days) {
      return days ? setDelayedDaysColor(days) : '';
    },
    setDeltaColorIfNegativeValue(deltaValue) {
      if (deltaValue < 0) return 'color: #C40C0C; text-align: end;';
      return 'text-align: end;';
    },
    async calcConsumptionItem(billing) {},
    async generateOVItem(billing) {},
    async generateBoletoItem(billing) {},
    /**
     * Método enviar email com a fatura
     * @param {Billing} billing
     * @return {function(): void}
     */
    async sendEmail(billing) {
      try {
        this.busy = true;
        if (billing.sendBoletoDate) {
          const { ok, errors } = await API.post('/execute', {
            path: `/invoice/send-mail/${this.search.month}/${this.search.year}/${billing.installationNumber}`,
            method: 'post',
            data: null,
          });
          if (!ok) {
            throw errors;
          }
        } else {
          // await API.post('/execute', {
          //   path:
          //     '/gen-email-item' +
          //     `/${this.search.month}/${this.search.year}/${billing.id}` +
          //     helpers.toQueryString(this.$route.query),
          //   method: 'post',
          //   data: billing,
          // });
        }
        this.$bvToast.toast('Email enviado', {
          title: `Sucesso`,
          variant: 'success',
          solid: true,
        });
        billing.emailSent = true;
        this.busy = false;
      } catch (ex) {
        this.busy = false;
        this.$bvToast.toast(helpers.getFirstError(ex), {
          title: `Erro`,
          variant: 'error',
          solid: true,
        });
      }
    },
    /**
     * Gera uma lista de resumo das faturas com base em seus status
     *
     * @param {Billing[]} billingList - Lista de faturas
     * @returns {BillingSummary[]} Lista de resumos das faturas
     */
    generateBillingSummaryList(billingList) {
      const billingSummaryStatusMap = new Map();
      billingSummaryStatusMap.set('TOTAL', {
        name: 'Total',
        total: billingList.length,
        percentageOfTotal: 100,
      });

      for (const billingStatus of Object.keys(INVOICE_STATUS)) {
        billingSummaryStatusMap.set(billingStatus, {
          name: INVOICE_STATUS[billingStatus],
          total: 0,
          percentageOfTotal: 0,
        });
      }

      billingList.forEach(billing => {
        const { billingStatus } = billing;
        const billingSummaryStatus = billingSummaryStatusMap.get(billingStatus);
        billingSummaryStatus.total += 1;
      });

      const totalOverall = billingSummaryStatusMap.get('TOTAL').total;
      billingSummaryStatusMap.forEach(statusSummary => {
        statusSummary.percentageOfTotal = Math.round(
          (statusSummary.total / totalOverall) * 100,
        );
      });

      return Array.from(billingSummaryStatusMap.values());
    },
  },
};
</script>

<style lang="scss" scoped>
.billing-table {
  display: block;
  max-height: 88vh !important;
  height: auto !important;
  margin-top: 1rem;

  th:first-child,
  td:first-child {
    z-index: 2;
    position: sticky;
    left: 0;
    background-color: #e7e7e7;
  }

  td:nth-child(2),
  th:nth-child(2) {
    z-index: 2;
    position: sticky;
    left: 6rem;
    background-color: #e7e7e7;
    min-width: 287px;
  }

  td:nth-child(3),
  th:nth-child(3) {
    z-index: 2;
    position: sticky;
    left: 24rem;
    background-color: #e7e7e7;
  }

  td:nth-child(4),
  th:nth-child(4) {
    z-index: 2;
    position: sticky;
    left: 30.5rem;
    background-color: #e7e7e7;
  }

  td:nth-child(5),
  th:nth-child(5) {
    z-index: 2;
    position: sticky;
    left: 37.5rem;
    background-color: #e7e7e7;
    min-width: 84px;
  }

  tr:nth-child(odd) td:nth-child(-n + 5) {
    background-color: #e7e7e7;
  }

  tr:nth-child(even) td:nth-child(-n + 5) {
    background-color: #f2f2f2;
  }
}

.billing-table .money-cell {
  text-align: right;
}
</style>
